import { useEffect, useRef, useState } from "react";
import { ConfirmHost } from "@shared/ui/confirm/ConfirmHost";
import { Toaster } from "@shared/ui/toast/Toaster";
import { notify } from "@shared/ui/toast/toastStore";
import { retrySave, useSaveFailure } from "@features/office";
import { SaveFailureBanner } from "@widgets/save-failure-banner";
import { usePaneManager } from "./store";
import { GenerationSettingPan } from "../../pages/generation-setting/GenerationSettingPan";
import { SlideResourcePan } from "../../pages/slide-resource/SlideResourcePan";
import { AssignmentResourcePan } from "../../pages/assignment-resource/AssignmentResourcePan";
import { HomePan } from "../../pages/home/HomePan";
import { FormatRegisterPan } from "../../pages/format-register/FormatRegisterPan";
import { GeneratePan } from "../../pages/generate/GeneratePan";
import { GenerationResultsPan } from "../../pages/generation-results/GenerationResultsPan";
import type { Pane } from "./types";

export function PaneShell() {
    const activePane = usePaneManager((s) => {
        const { panes, activePaneUid } = s;
        return panes.find((p) => p.uid === activePaneUid) ?? panes[0];
    });

    return (
        <div className="relative flex h-screen flex-col bg-page-bg font-sans text-text-primary">
            {/* 設定をファイルへ保存できていないことを、どの画面にいても出す。画面ごとに置くと
                取りこぼす（設定はスライドの画面でも担当の画面でも書かれる）。 */}
            <SaveFailureNotice />
            <PaneContent pane={activePane} />
            <Toaster />
            {/* 確認ダイアログの表示ホスト。置き忘れると confirm() の約束が解決されず、削除が
                無反応になる（押した分の保留が残り、以降の確認も即キャンセル扱いになる）。 */}
            <ConfirmHost />
        </div>
    );
}

/**
 * 保存の失敗を帯へ繋ぐ。帯そのものは状態を持たないので、購読とやり直しをここが担う。
 *
 * 設計: [[2026-09-15 設計ノート アドイン 設定の保存失敗の申告]]。
 */
function SaveFailureNotice() {
    const failure = useSaveFailure();
    const [busy, setBusy] = useState(false);
    const wasFailing = useRef(false);

    // **直ったことを言う。** 帯が消えるだけだと、直ったのか見落としたのか区別がつかない。
    // 手で押した場合も、あとから設定を書いて自動で通った場合も同じ扱いにしたいので、
    // 押した側ではなく状態の遷移で出す（UI 同期の useEffect）。
    useEffect(() => {
        if (failure) {
            wasFailing.current = true;
            return;
        }
        if (wasFailing.current) {
            wasFailing.current = false;
            notify("設定を保存しました", "success");
        }
    }, [failure]);

    if (!failure) return null;

    async function handleRetry(): Promise<void> {
        setBusy(true);
        try {
            // 通れば購読が失敗を消すので、ここで成功を言う必要はない（上の遷移が拾う）。
            await retrySave();
        } finally {
            setBusy(false);
        }
    }

    return (
        <SaveFailureBanner
            reason={failure.message}
            busy={busy}
            onRetry={() => {
                void handleRetry();
            }}
        />
    );
}

function PaneContent({ pane }: { pane: Pane }) {
    switch (pane.type) {
        case "home":
            return <HomePan />;
        case "generation-setting":
            return <GenerationSettingPan />;
        case "slide":
            return <SlideResourcePan />;
        case "assignment":
            // **担当ごとに実体を作り直す。** 鍵が無いと、要素の型と位置が変わらないので React が
            // 同じ実体を使い回し、画面の `useState` が担当を移っても残る。新しい担当（ストアに
            // 実体が無い状態）へ移ったとき前の下書きの本文と対象が入ったまま出て、ラベルを 1 文字
            // 打つとその中身が 2 つ目の担当として保存されていた（記録 55）。同じ使い回しは
            // `useScrollMemory` のコメントが実機の観察として記録している。
            return <AssignmentResourcePan key={pane.id} id={pane.id} />;
        case "format":
            return <FormatRegisterPan />;
        case "generate":
            return <GeneratePan />;
        case "results":
            return <GenerationResultsPan />;
    }
}
